import { useEffect, useRef, useState } from "react";
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  canAccess, roleAccess, salesOrderDraftSchema,
  type AccessVerb, type SalesOrderAction, type SalesOrderDto, type SalesOrderInput,
  vnDate,
} from "@hisense/shared";
import { useSalesOrder, useSoSource, useSoSources } from "@/api/salesOrders";
import { useAuth } from "@/app/auth";
import { useMenuAccess } from "@/app/access";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { PageActions, PageBody, PageHeader } from "@/components/ui/PageHeader";
import { FieldLabel, Input } from "@/components/ui/Input";
import { SearchableFilter, type SearchableFilterOption } from "@/components/ui/SearchableFilter";
import { Button } from "@/components/ui/Button";
import { SaveButton } from "@/components/ui/SaveButton";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { FieldGrid, FormGroup, Hint, SPAN_CLASS } from "@/components/ui/FormLayout";
import { DeliveryWavesPanel } from "@/components/opportunities/DeliveryWavesPanel";
import { SalesOrderRules } from "@/components/salesOrders/SalesOrderRules";
import { usePageCrumbs } from "@/components/shell/breadcrumb";

/*
 * Trang Đơn bán hàng (19/09/2026 — năm quy tắc đã chốt, cùng chuẩn biểu mẫu với báo giá / đơn mua): các nhóm PI nguồn, Kế hoạch, Dòng
 * hàng, Phân kỳ thanh toán, Hạn mức công nợ, Lịch giao từng đợt, Lịch sử; một thanh thao tác dính đáy. Mỗi nút hiện theo ĐÚNG ô tick
 * của menu Đơn bán hàng (cùng bộ giải quyền máy chủ dùng): Tạo / Sửa cho người lập kế hoạch, Duyệt cho người xác nhận, Xoá để lưu trữ.
 * Không đặt nút "Hủy" (rời trang) ở thanh đáy như trang khác: đứng cạnh "Huỷ đơn" thì dễ bấm nhầm — rời trang bằng liên kết đầu trang.
 */

type SalesOrderDraft = Omit<SalesOrderInput, "creditHold"> & { instalments: NonNullable<SalesOrderInput["instalments"]> };
type ActionKey = SalesOrderAction["action"] | "archive";

const draftFrom = (r?: SalesOrderDto, piId = ""): SalesOrderDraft => ({
  requestId: r?.id ?? crypto.randomUUID(),
  piId: r?.definition?.source.piId ?? piId,
  orderDate: r?.definition?.orderDate ?? vnDate(),
  plannedDate: r?.definition?.plannedDate ?? "",
  releaseBasis: r?.definition?.releaseBasis ?? "",
  notes: r?.definition?.notes ?? "",
  instalments: r?.instalments ?? [],
});

/** Câu giải thích trong hộp thao tác — nói trước hậu quả của từng nút. */
const ACTION_HINT: Record<ActionKey, string> = {
  refresh: "so.refreshHint", confirm: "so.confirmHint", reschedule: "so.rescheduleHint", amend: "so.actionHint",
  close: "so.closeHint", cancel: "so.cancelHint", archive: "so.archiveHint",
};

export function SalesOrderEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const query = useSalesOrder(id);
  const { t } = useTranslation();
  if (id && query.isLoading) return <LoadingState />;
  if (id && (query.error || !query.data)) {
    return <ErrorState message={query.error ? getLocalizedError(query.error, t, "so.error") : t("so.error")} onRetry={() => void query.refetch()} />;
  }
  return <SalesOrderForm key={id ?? params.get("piId") ?? "new"} existing={query.data?.data} initialPi={params.get("piId") ?? ""} />;
}

function SalesOrderForm({ existing, initialPi }: { existing?: SalesOrderDto; initialPi: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [saved, setSaved] = useState(existing);
  const [draft, setDraft] = useState(() => draftFrom(existing, initialPi));
  const [dirty, setDirty] = useState(false);
  const [wavesDirty, setWavesDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState<ActionKey | null>(null);
  const [reason, setReason] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [creditForm, setCreditForm] = useState({ limit: existing?.credit?.limit ?? "", currency: existing?.credit?.currency ?? "", basis: existing?.credit?.basis ?? "" });
  const lock = useRef(false);
  const allowLeave = useRef(false);

  // Cùng bộ giải quyền với máy chủ (packages/shared/src/access.ts) — chỉ để khỏi vẽ nút mà bấm vào là 403.
  const access = user ? user.access ?? roleAccess(user.role) : null;
  const can = (verb: AccessVerb) => !!access && canAccess(access, "salesOrders", verb);
  // Hạn mức công nợ là quyết định tiền → ô Duyệt của menu Đơn bán hàng, không phải vai trò
  // quản trị viên (sửa 20/09/2026 cùng lúc với `saveCredit` ở máy chủ).
  const canSetCredit = useMenuAccess("salesOrders")("approve");
  const legacy = !!saved && !saved.definition;
  const archived = !!saved?.archived;
  const status = saved?.status;
  const canEdit = !archived && !legacy && (saved ? status === "Draft" && can("update") : can("create"));
  // Phân kỳ: sửa cùng nháp; đơn đã xác nhận sửa bằng thao tác "Sửa phân kỳ" (có lý do, có lịch sử).
  const canSchedule = canEdit || (!archived && status === "Confirmed" && can("update"));

  const choices = useSoSources(search, !saved);
  const sourceQuery = useSoSource(draft.piId, !saved);
  const source = saved?.definition?.source ?? sourceQuery.data?.data;

  usePageCrumbs([{ label: saved?.soNo ?? t("so.new") }]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (dirty || wavesDirty || busy) && !allowLeave.current &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if ((dirty || wavesDirty || busy) && !allowLeave.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty, wavesDirty, busy]);

  const change = (patch: Partial<SalesOrderDraft>) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); allowLeave.current = false; };
  const accept = (r: SalesOrderDto) => {
    client.setQueryData(["sales-order", r.id], { data: r });
    setSaved(r);
    setDraft(draftFrom(r));
    setDirty(false);
    setCreditForm({ limit: r.credit?.limit ?? "", currency: r.credit?.currency ?? r.definition?.source.currency ?? "", basis: r.credit?.basis ?? "" });
  };
  const invalidate = () => Promise.all(["sales-orders", "so-sources"].map((key) => client.invalidateQueries({ queryKey: [key] })));
  const declared = () => draft.instalments.filter((x) => x.dueDate && x.amount);

  /**
   * Dòng cho ô chọn PI. Luôn giữ PI ĐANG CHỌN trong danh sách kể cả khi nó rơi khỏi kết quả tìm kiếm hiện tại — nếu không, ô sẽ hiện id
   * thô thay vì số PI (đã thấy đúng vậy khi khách hàng của PI bị lưu trữ).
   */
  const rows: SearchableFilterOption[] = (choices.data?.data ?? []).map((p) => ({ value: p.id, label: `${p.piNo} · ${p.customerName}`, sublabel: p.customerPoNo ?? undefined }));
  const piOptions: SearchableFilterOption[] = draft.piId && !rows.some((o) => o.value === draft.piId) && source
    ? [{ value: source.piId, label: `${source.piNo} · ${source.customerName}`, sublabel: source.customerPoNo }, ...rows]
    : rows;

  const save = async () => {
    if (lock.current || !canEdit || wavesDirty) return;
    const parsed = salesOrderDraftSchema.safeParse({ ...draft, instalments: declared() });
    if (!parsed.success || !source) { setError(t("so.invalid")); return; }
    lock.current = true; setBusy(true); setError(null);
    try {
      const r = await apiFetch<{ data: SalesOrderDto }>(saved ? `/sales-orders/${saved.id}` : "/sales-orders", {
        method: saved ? "PUT" : "POST",
        body: saved ? { ...parsed.data, revision: saved.revision } : parsed.data,
      });
      accept(r.data);
      setSavedAt(Date.now());
      await invalidate();
      if (!saved) { allowLeave.current = true; navigate(`/sales-orders/${r.data.id}`, { replace: true }); }
    } catch (e) {
      setError(getLocalizedError(e, t, "so.error"));
    } finally { lock.current = false; setBusy(false); }
  };

  const openAction = (a: ActionKey) => {
    setError(null);
    // Xác nhận: căn cứ người lập đã ghi được điền sẵn, người duyệt xem lại rồi ghi căn cứ cuối cùng.
    setReason(a === "confirm" ? draft.releaseBasis ?? "" : "");
    setNextDate(a === "reschedule" ? saved?.definition?.plannedDate ?? "" : "");
    setAction(a);
  };
  const needsDate = action === "confirm" && !saved?.definition?.plannedDate;
  const actionReady = reason.trim().length >= 5 && !needsDate &&
    (action !== "reschedule" || (!!nextDate && nextDate !== saved?.definition?.plannedDate));
  const act = async () => {
    if (!saved || !action || lock.current || wavesDirty || (dirty && action !== "amend") || !actionReady) return;
    lock.current = true; setBusy(true); setError(null);
    try {
      const body = action === "archive"
        ? { reason }
        : { revision: saved.revision, action, reason, instalments: action === "amend" ? declared() : undefined, plannedDate: action === "reschedule" ? nextDate : undefined };
      const r = await apiFetch<{ data: SalesOrderDto }>(`/sales-orders/${saved.id}/${action === "archive" ? "archive" : "actions"}`, { method: "POST", body });
      accept(r.data);
      await invalidate();
      setAction(null);
      setReason("");
    } catch (e) {
      // Lỗi hiện ngay trong hộp thao tác, hộp không tự đóng — người dùng thấy vì sao và sửa được lý do / ngày.
      setError(getLocalizedError(e, t, "so.error"));
    } finally { lock.current = false; setBusy(false); }
  };

  const saveCredit = async () => {
    if (!canSetCredit || !source || archived || lock.current || dirty || wavesDirty) return;
    lock.current = true; setBusy(true); setError(null);
    try {
      await apiFetch(`/sales-orders/credit/${source.customerId}`, {
        method: "PUT",
        body: { requestId: crypto.randomUUID(), customerId: source.customerId, currency: creditForm.currency || source.currency, limit: creditForm.limit.trim() || null, basis: creditForm.basis },
      });
      if (saved) { const r = await apiFetch<{ data: SalesOrderDto }>(`/sales-orders/${saved.id}`); accept(r.data); }
    } catch (e) {
      setError(getLocalizedError(e, t, "so.error"));
    } finally { lock.current = false; setBusy(false); }
  };

  // Thứ tự trên thanh đáy: việc phụ trước, việc chính (Xác nhận) sau cùng — sát tay phải như nút Lưu ở các trang khác.
  const actions: ActionKey[] = [];
  if (saved && !archived && !legacy) {
    if (status === "Draft" && can("update")) actions.push("refresh");
    if (status === "Confirmed" && can("update")) actions.push("reschedule", "amend", "close");
    if ((status === "Draft" || status === "Confirmed") && can("update")) actions.push("cancel");
    if ((status === "Closed" || status === "Cancelled") && can("delete")) actions.push("archive");
    if (status === "Draft" && can("approve")) actions.push("confirm");
  }

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog open={blocker.state === "blocked"} title={t("common.unsavedTitle")} description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")} cancelLabel={t("common.keepEditing")} busy={busy}
        onCancel={() => blocker.reset?.()} onConfirm={() => { allowLeave.current = true; blocker.proceed?.(); }} />
      <PageHeader
        title={saved?.soNo ?? t("so.new")}
        subtitle={legacy ? t("so.legacy") : saved && source ? `${source.piNo} · ${source.customerName}` : t("so.intro")}
        actions={<PageActions><Link className="text-sm text-brand underline" to="/sales-orders">{t("so.back")}</Link></PageActions>}
      />
      <SalesOrderRules />
      {saved && !legacy && !archived && status === "Draft" && !canEdit && (
        <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{t("errors.readOnly")}</p>
      )}
      {saved?.sourceChanged && <p role="status" className="rounded-sm border border-warn/30 bg-warn-tint px-3 py-2 text-sm">{t("so.sourceChanged")}</p>}

      {!legacy && (
        <>
          {/* ── PI nguồn — chọn khi lập đơn; đã lưu thì cố định (một PI một đơn). ── */}
          <FormGroup id="so-group-source" title={t("so.groups.source")} hint={t("so.groups.sourceHint")}>
            <FieldGrid>
              <div className={SPAN_CLASS[8]}>
                <FieldLabel htmlFor="so-pi">{t("so.source")}</FieldLabel>
                {saved ? (
                  <p id="so-pi" className="font-mono tabular text-sm">{source ? `${source.piNo} · ${source.customerName}` : draft.piId}</p>
                ) : (
                  <SearchableFilter className="w-full" label={t("so.source")} allLabel={t("warehouse.select")} includeAll={false}
                    value={draft.piId} options={piOptions}
                    searchPlaceholder={t("so.searchSource")} searchValue={search} onSearchChange={setSearch}
                    onChange={(piId) => change({ piId, releaseBasis: "" })}
                    isLoading={choices.isLoading} error={choices.error ? getLocalizedError(choices.error, t, "so.error") : null} onRetry={() => void choices.refetch()}
                    footer={choices.data?.hasMore ? () => <p className="px-2 py-1 text-2xs text-ink-soft">{t("so.more")}</p> : undefined} />
                )}
              </div>
              {source && (
                <div className={`${SPAN_CLASS[4]} flex items-end`}>
                  <Link className="text-sm text-brand underline" to={`/proforma-invoices/${source.piId}`}>{t("so.sourceLink")}</Link>
                </div>
              )}
            </FieldGrid>
            {!saved && sourceQuery.error && <ErrorState message={getLocalizedError(sourceQuery.error, t, "so.error")} onRetry={() => void sourceQuery.refetch()} />}
            {source && (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 rounded-sm border border-line bg-sunken/40 px-3 py-2.5 text-sm @md:grid-cols-3">
                <div><dt className="text-xs text-ink-faint">{t("so.customer")}</dt><dd className="text-ink">{source.customerName}</dd></div>
                <div><dt className="text-xs text-ink-faint">{t("so.customerPo")}</dt><dd className="font-mono tabular text-ink">{source.customerPoNo || "—"}</dd></div>
                <div><dt className="text-xs text-ink-faint">{t("so.pProcess")}</dt><dd className="text-ink">{source.pProcess ? t("so.yes") : t("so.no")}</dd></div>
              </dl>
            )}
          </FormGroup>

          {/* ── Kế hoạch — ngày dự kiến xuất (MRP lập kế hoạch theo ngày này) và căn cứ xác nhận. ── */}
          <FormGroup id="so-group-plan" title={t("so.groups.plan")} hint={t("so.groups.planHint")}
            actions={actions.includes("reschedule") ? (
              // Đơn đã xác nhận: ngày không sửa thẳng mà đổi bằng thao tác có lý do — lịch sử ghi ngày cũ → ngày mới. Đặt ngoài fieldset
              // (fieldset khoá khi đơn không còn nháp, sẽ khoá luôn nút này).
              <Button type="button" variant="secondary" size="sm" disabled={busy || dirty || wavesDirty} onClick={() => openAction("reschedule")}>{t("so.reschedule")}</Button>
            ) : undefined}>
            <fieldset disabled={busy || !canEdit} className="min-w-0">
              <FieldGrid>
                <div className={SPAN_CLASS[4]}>
                  <FieldLabel htmlFor="so-orderDate">{t("so.orderDate")}</FieldLabel>
                  <Input id="so-orderDate" type="date" value={draft.orderDate} onChange={(e) => change({ orderDate: e.target.value })} />
                </div>
                <div className={SPAN_CLASS[4]}>
                  <FieldLabel htmlFor="so-plannedDate">{t("so.plannedDate")}</FieldLabel>
                  <Input id="so-plannedDate" type="date" value={draft.plannedDate} onChange={(e) => change({ plannedDate: e.target.value })} />
                  <Hint>{t("so.plannedDateHint")}</Hint>
                </div>
                <div className={SPAN_CLASS[4]} />
                <div className={SPAN_CLASS[12]}>
                  <FieldLabel htmlFor="so-releaseBasis">{t("so.basis")}</FieldLabel>
                  <Input id="so-releaseBasis" value={draft.releaseBasis ?? ""} onChange={(e) => change({ releaseBasis: e.target.value })} />
                  <Hint>{t("so.basisHint")}</Hint>
                </div>
                <div className={SPAN_CLASS[12]}>
                  <FieldLabel htmlFor="so-notes">{t("so.notes")}</FieldLabel>
                  <Input id="so-notes" value={draft.notes ?? ""} onChange={(e) => change({ notes: e.target.value })} />
                </div>
              </FieldGrid>
            </fieldset>
          </FormGroup>

          {/* ── Dòng hàng — lấy nguyên từ PI, chỉ xem. ── */}
          {source && (
            <FormGroup id="so-group-lines" flush title={t("so.lines")} hint={t("so.linesHint")}>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[46rem] table-fixed text-left text-sm">
                  <colgroup>
                    <col className="w-10" /><col /><col className="w-28" /><col className="w-28" /><col className="w-28" /><col className="w-44" />
                  </colgroup>
                  <thead className="bg-sunken text-xs text-ink-soft">
                    <tr>
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">{t("so.product")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("so.quantity")}</th>
                      <th className="px-3 py-2 font-medium">{t("so.unit")}</th>
                      <th className="px-3 py-2 font-medium">{t("so.lot")}</th>
                      <th className="px-3 py-2 font-medium">{t("so.chosen")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {source.lines.map((l, i) => {
                      const snap = saved?.definition?.pricing.find((p) => p.lineId === l.id);
                      return (
                        <tr key={l.id} data-so-line className="border-t border-line align-top">
                          <td className="px-3 py-2 font-mono tabular text-ink-soft">{i + 1}</td>
                          <td className="min-w-0 px-3 py-2">
                            <p className="truncate font-mono tabular" title={`${l.productCode} / ${l.subRef}`}>{l.productCode}{l.subRef ? ` / ${l.subRef}` : ""}</p>
                            {l.description && <p className="truncate text-xs text-ink-soft" title={l.description}>{l.description}</p>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular">{l.quantity}</td>
                          <td className="px-3 py-2">
                            {l.unit}
                            {snap && snap.baseUnit !== snap.documentUnit && <span className="block font-mono tabular text-xs text-ink-soft">{snap.quantityInBase} {snap.baseUnit}</span>}
                          </td>
                          <td className="px-3 py-2 font-mono tabular">{l.lotNumber || "—"}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono tabular">{snap?.chosen ?? "—"}</span>
                            <span className="block line-clamp-2 text-xs text-ink-soft" title={snap?.explanation ?? t("so.pricing")}>{snap?.explanation ?? t("so.pricing")}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </FormGroup>
          )}

          {/* ── Phân kỳ thanh toán — không bắt buộc; đã khai thì tổng phải đúng tổng PI. ── */}
          {source && (
            <FormGroup id="so-group-instalments" title={t("so.instalments")} hint={t("so.instalmentsHint")}>
              {draft.instalments.map((row, i) => (
                <div key={row.id} className="grid gap-2 @md:grid-cols-[12rem_12rem_auto]">
                  <Input type="date" aria-label={t("so.due")} value={row.dueDate} disabled={busy || !canSchedule}
                    onChange={(e) => { const next = [...draft.instalments]; next[i] = { ...row, dueDate: e.target.value }; change({ instalments: next }); }} />
                  <Input className="font-mono" inputMode="decimal" aria-label={t("so.amount")} value={row.amount} disabled={busy || !canSchedule}
                    onChange={(e) => { const next = [...draft.instalments]; next[i] = { ...row, amount: e.target.value }; change({ instalments: next }); }} />
                  {canSchedule && (
                    <Button type="button" size="sm" variant="secondary" className="justify-self-start" disabled={busy}
                      onClick={() => change({ instalments: draft.instalments.filter((_, j) => j !== i) })}>{t("so.removeInstalment")}</Button>
                  )}
                </div>
              ))}
              {canSchedule && (
                <Button type="button" size="sm" variant="secondary" disabled={busy}
                  onClick={() => change({ instalments: [...draft.instalments, { id: crypto.randomUUID(), dueDate: "", amount: "" }] })}>{t("so.addInstalment")}</Button>
              )}
              {!canSchedule && draft.instalments.length === 0 && <p className="text-sm text-ink-soft">—</p>}
            </FormGroup>
          )}

          {/* ── Hạn mức công nợ — chỉ chặn khi khách có khai; chỉ admin khai. ── */}
          {saved && source && (
            <FormGroup id="so-group-credit" title={t("so.groups.credit")} hint={t("so.creditHint")}>
              <p className="text-sm text-ink">
                {saved.credit?.limit != null
                  ? t("so.creditDeclared", { limit: saved.credit.limit, currency: saved.credit.currency })
                  : t("so.noCreditPolicy")}
              </p>
              {canSetCredit && !archived && (
                <FieldGrid>
                  <div className={SPAN_CLASS[4]}>
                    <FieldLabel htmlFor="so-credit-limit">{t("so.creditLimit")}</FieldLabel>
                    <Input id="so-credit-limit" className="font-mono" inputMode="decimal" value={creditForm.limit} disabled={busy} onChange={(e) => setCreditForm((f) => ({ ...f, limit: e.target.value }))} />
                  </div>
                  <div className={SPAN_CLASS[4]}>
                    <FieldLabel htmlFor="so-credit-currency">{t("so.creditCurrency")}</FieldLabel>
                    <Input id="so-credit-currency" className="font-mono" value={creditForm.currency || source.currency} disabled={busy} onChange={(e) => setCreditForm((f) => ({ ...f, currency: e.target.value }))} />
                  </div>
                  <div className={SPAN_CLASS[12]}>
                    <FieldLabel htmlFor="so-credit-basis">{t("so.creditBasis")}</FieldLabel>
                    <Input id="so-credit-basis" value={creditForm.basis} disabled={busy} onChange={(e) => setCreditForm((f) => ({ ...f, basis: e.target.value }))} />
                  </div>
                  <div className={SPAN_CLASS[12]}>
                    <Button type="button" size="sm" variant="secondary" disabled={busy || dirty || wavesDirty || creditForm.basis.trim().length < 5} onClick={() => void saveCredit()}>{t("so.saveCredit")}</Button>
                  </div>
                </FieldGrid>
              )}
            </FormGroup>
          )}

          {saved?.definition && (
            <DeliveryWavesPanel order={saved} canEdit={!archived && ["Draft", "Confirmed"].includes(saved.status) && can("update")}
              documentDirty={dirty || busy} onDirtyChange={setWavesDirty} />
          )}

          {saved && (
            <FormGroup id="so-group-history" title={t("so.history")}>
              <ol className="space-y-1.5 text-sm">
                {saved.events.map((e, i) => (
                  <li key={i} className="flex flex-wrap gap-x-2">
                    <span className="font-mono tabular text-xs text-ink-faint">{new Date(e.createdAt).toLocaleString()}</span>
                    <span className="text-ink-soft">{e.actorName}</span>
                    <span className="font-medium text-ink">{t(`so.${e.action}`)}</span>
                    {e.reason && <span className="min-w-0 text-ink-soft">· {e.reason}</span>}
                  </li>
                ))}
              </ol>
            </FormGroup>
          )}
        </>
      )}

      {error && !action && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
        <p className="mr-auto text-sm">
          {saved ? <>{t(`so.${saved.status}`)}{archived && <> · {t("so.Archived")}</>}</> : null}
        </p>
        {canEdit && <SaveButton onClick={() => void save()} saving={busy && !action} savedAt={savedAt} disabled={busy || wavesDirty || !dirty} />}
        {actions.filter((a) => a !== "reschedule").map((a) => (
          <Button key={a} type="button" size="sm" variant={a === "confirm" ? "primary" : "secondary"}
            disabled={busy || wavesDirty || (dirty && a !== "amend")} onClick={() => openAction(a)}>{t(`so.${a}`)}</Button>
        ))}
      </div>

      {action && (
        <Modal title={t(`so.${action}`)} closeLabel={t("common.close")} onClose={() => { if (!busy) { setAction(null); setError(null); } }} className="max-w-lg">
          <div className="space-y-4 p-4">
            <p className="text-sm">{t(ACTION_HINT[action])}</p>
            {needsDate && <p role="alert" className="rounded-sm border border-warn/30 bg-warn-tint px-3 py-2 text-sm">{t("so.confirmNeedsDate")}</p>}
            {action === "reschedule" && (
              <div>
                <FieldLabel htmlFor="so-action-date">{t("so.newPlannedDate")}</FieldLabel>
                <Input id="so-action-date" type="date" value={nextDate} disabled={busy} onChange={(e) => setNextDate(e.target.value)} />
              </div>
            )}
            <div>
              <FieldLabel htmlFor="so-action-reason">{action === "confirm" ? t("so.basis") : t("so.reason")}</FieldLabel>
              <Input id="so-action-reason" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
              {action === "confirm" && <Hint>{t("so.basisHint")}</Hint>}
            </div>
            {error && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
            <div className="flex justify-end">
              <Button disabled={busy || !actionReady} onClick={() => void act()}>{t("so.confirmAction")}</Button>
            </div>
          </div>
        </Modal>
      )}
    </PageBody>
  );
}
